import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, UserPlus, Users, Trash2, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'super_admin',         label: 'Super Admin' },
  { value: 'user_admin',          label: 'User Admin' },
  { value: 'procurement_team',    label: 'Procurement Team' },
  { value: 'director_procurement',label: 'Director Procurement' },
  { value: 'finance_team',        label: 'Finance Team' },
  { value: 'finance_manager',     label: 'Finance Manager' },
];

// Random 12-char temp password with upper, lower, digit and symbol
const generateTempPassword = () => {
  const sets = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnpqrstuvwxyz', '23456789', '@#$%&*!'];
  const all = sets.join('');
  const rand = (n) => crypto.getRandomValues(new Uint32Array(1))[0] % n;
  const chars = sets.map((s) => s[rand(s.length)]);
  while (chars.length < 12) chars.push(all[rand(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) { const j = rand(i + 1); [chars[i], chars[j]] = [chars[j], chars[i]]; }
  return chars.join('');
};

const emptyUser = () => ({ email: '', name: '', role: 'procurement_team', temp_password: generateTempPassword() });

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState(emptyUser);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.name || !newUser.temp_password) {
      toast.error('Fill all fields');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/users`, newUser);
      toast.success(`User ${r.data.email} created. Share the temporary password.`);
      setLastCreated(r.data);
      setNewUser(emptyUser());
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? 'User deactivated' : 'User reactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They won't be able to log in until reactivated.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="animate-fadeIn" data-testid="user-management-page">
      <div className="page-header">
        <h1 className="page-title"><Users className="inline w-6 h-6 mr-2" /> User Management</h1>
        <p className="page-subtitle">Invite team members and assign roles</p>
      </div>

      {/* Create User Form */}
      <div className="form-card" style={{ marginBottom: 24 }}>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <UserPlus className="w-4 h-4" style={{ color: 'var(--navy)' }} />
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>Add New User</div>
          </div>
          <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 12, alignItems: 'end' }}>
            <div>
              <Label>Email</Label>
              <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                     placeholder="user@company.com" required type="email" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Full Name</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                     placeholder="Jane Doe" required data-testid="new-user-name" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temp Password</Label>
              <Input value={newUser.temp_password} onChange={(e) => setNewUser({ ...newUser, temp_password: e.target.value })}
                     placeholder="Auto-generated" required data-testid="new-user-password" />
            </div>
            <Button type="submit" disabled={creating} data-testid="create-user-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
            </Button>
          </form>
          {lastCreated && (
            <div style={{ marginTop: 12, padding: 12, background: '#e8f5e9', borderRadius: 6, fontSize: 13 }}>
              ✅ <b>{lastCreated.email}</b> created. Share these credentials — user should change password on first login:
              <br /><code>Password: {lastCreated.temp_password}</code>
            </div>
          )}
        </div>
      </div>

      {/* Users List */}
      <div className="form-card">
        <div style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)', marginBottom: 12 }}>All Users ({users.length})</div>
          <table className="contract-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                  <td style={{ fontFamily: "'DM Mono', monospace" }}>{u.email}</td>
                  <td>{u.name}</td>
                  <td>
                    {u.role === 'super_admin' ? (
                      <span className="badge" style={{ background: '#fce4ec' }}><Shield className="inline w-3 h-3 mr-1" />{u.role_label}</span>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u.id, v)} disabled={u.id === currentUser?.id}>
                        <SelectTrigger style={{ height: 30, fontSize: 12 }}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.filter(r => r.value !== 'super_admin').map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>
                    {u.active ? <span className="badge badge-active">Active</span> : <span className="badge">Inactive</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    {u.id !== currentUser?.id && u.role !== 'super_admin' && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleToggleActive(u)} style={{ marginRight: 4 }}>
                          {u.active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
